import type { FC, ReactNode } from "react";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import type { SxProps, Theme } from "@mui/material/styles";
import { useHoverPopover } from "../../shared/HoverPopover";
import { InfoIcon } from "../../shared/icons";
import type { InfoPopoverContent } from "./InfoPopover";
import { InfoPopover } from "./InfoPopover";

type InlineFieldProps = { label?: string; input: ReactNode; htmlFor?: string };

const PRIMARY_LABEL_WIDTH = "35%";

type InlineFieldPrimaryProps = { infoPopoverContent?: InfoPopoverContent } & InlineFieldProps;
export const InlineFieldPrimary: FC<InlineFieldPrimaryProps> = ({ label, input, htmlFor, infoPopoverContent }) => {
  const { anchorProps, popoverProps } = useHoverPopover();

  const showInfoPopover = infoPopoverContent && Object.values(infoPopoverContent).some(value => Boolean(value));
  return (
    <>
      <InlineFieldLayout
        labelWidth={PRIMARY_LABEL_WIDTH}
        label={
          <InlineFieldLabel sx={{ textWrap: "balance" }} htmlFor={htmlFor}>
            {label}

            {showInfoPopover && (
              <Box
                component="span"
                {...anchorProps}
                sx={{
                  "height": "100%",
                  "position": "relative",
                  "ml": 1,

                  "&:before": {
                    content: "''",
                    position: "absolute",
                    inset: 0,
                    transform: "scaleX(3.5) scaleY(2.3)",
                  },
                }}
              >
                <InfoIcon strokeWidth={2.5} size={10} color={theme => theme.palette.primary.main} />
              </Box>
            )}
          </InlineFieldLabel>
        }
        input={input}
      />
      {infoPopoverContent && <InfoPopover {...infoPopoverContent} {...popoverProps} />}
    </>
  );
};

const SECONDARY_LABEL_WIDTH = "60%";

export const InlineFieldSecondary: FC<InlineFieldProps & { subLabel?: string }> = ({
  label,
  input,
  htmlFor,
  subLabel,
}) => {
  return (
    <InlineFieldLayout
      labelWidth={SECONDARY_LABEL_WIDTH}
      label={
        <Stack component="label" htmlFor={htmlFor} sx={{ pb: 2 }}>
          <InlineFieldLabel htmlFor={htmlFor} sx={{ fontWeight: "600", color: theme => theme.palette.text.primary }}>
            {label}
          </InlineFieldLabel>
          {subLabel && (
            <Typography
              component="span"
              sx={{
                mt: 0.5,
                textWrap: "pretty",
                color: theme => theme.palette.text.tertiary,
                fontSize: theme => theme.typography.pxToRem(12),
              }}
            >
              {subLabel}
            </Typography>
          )}
        </Stack>
      }
      input={input}
    />
  );
};

const InlineFieldLayout: FC<{ label: ReactNode; input: ReactNode; labelWidth: string }> = ({
  label,
  input,
  labelWidth,
}) => {
  return (
    <Box
      sx={{
        width: "100%",
        display: "grid",
        gridTemplateColumns: `${labelWidth} minmax(0, 1fr)`,
        gap: 3,
        mt: 0.5,
      }}
    >
      <Box
        sx={{
          display: "inline-flex",
          /** This is in order to align the label with the input field so that is looks good on multi lines inputs as well */ py: 1.1,
        }}
      >
        {label}
      </Box>
      {input}
    </Box>
  );
};

const InlineFieldLabel: FC<{ children: ReactNode; htmlFor?: string; sx?: SxProps<Theme> }> = ({
  children,
  htmlFor,
  sx,
}) => {
  return (
    <Typography
      component="label"
      htmlFor={htmlFor}
      /** We don't want to trigger focus on the input when label is clicked */
      onClick={e => e.preventDefault()}
      sx={{
        fontSize: theme => theme.typography.pxToRem(14),
        color: theme => theme.palette.text.tertiary,
        overflowWrap: "anywhere",
        hyphens: "auto",
        ...sx,
      }}
    >
      {children}
    </Typography>
  );
};
